import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What is a Form 12 legal advice report?",
    a: "It is the approved form under the Retail Shop Leases Act 1994 (Qld) in which a lawyer certifies that they have explained the retail shop lease — and, on an assignment, the assignment documents — to the incoming tenant. The lawyer signs it, not the tenant, and it is given to the landlord as part of the consent process.",
  },
  {
    q: "How quickly can you provide a Form 12?",
    a: `Most reports are turned around within 1 to 2 business days of receiving the lease, the disclosure statement and the deed of assignment, and faster where a settlement date is under pressure. Call ${FIRM.phone} or email the documents and we will confirm the fee and the timing the same day.`,
  },
  {
    q: "What does a Form 12 cost?",
    a: "A lease review and Form 12 legal advice report is a fixed $880 including GST. Standalone reports are quoted separately from a full business purchase, and there is no obligation to engage us for the rest of the transaction.",
  },
  {
    q: "Do I need a Form 12 for every commercial lease?",
    a: "No. The requirement arises under the Retail Shop Leases Act, which applies to retail shops and premises in retail shopping centres — not to every commercial tenancy. Large premises above the floor-area threshold and leases to listed corporations and their subsidiaries are among the exclusions. We confirm whether the Act applies before anyone spends money on forms.",
  },
  {
    q: "What is the difference between the Form 9 and the Form 12?",
    a: "The financial advice report comes from the incoming tenant's accountant and deals with the financial obligations under the lease. The Form 12 legal advice report comes from a lawyer and deals with the legal obligations. Landlords generally want both before consenting to an assignment.",
  },
  {
    q: "How long does landlord consent to a retail shop lease assignment take?",
    a: "Two to six weeks is normal, and shopping-centre landlords sit at the longer end. The clock does not start until the landlord has the assignee's financial and business references, the disclosure statement and the advice reports. Contract dates should be set with that in mind.",
  },
  {
    q: "Can the outgoing tenant be released from the lease?",
    a: "Under the Act an assignor who has complied with the disclosure obligations is generally released from liability for the balance of the term on assignment of a retail shop lease. That release depends on the disclosure being done properly, which is why sellers should not treat the forms as paperwork.",
  },
];

const WE_ACT_FOR = [
  {
    title: "Incoming tenants and business buyers",
    body: "You are taking on the largest fixed commitment the business has. We read the lease with you — term, options, rent reviews, outgoings, make-good, guarantees — and issue the Form 12 legal advice report the landlord needs before consent.",
  },
  {
    title: "Outgoing tenants and business sellers",
    body: "Getting the disclosure right is what releases you from liability for the balance of the term. We prepare the assignor's disclosure, deal with the landlord's solicitors and keep the consent process moving to your settlement date.",
  },
  {
    title: "Business brokers and franchisors",
    body: "You need the forms done quickly and correctly, by someone who answers the phone. We take standalone Form 12 referrals on a fixed fee and report back on turnaround so you can hold the settlement date.",
  },
  {
    title: "Landlords and centre managers",
    body: "Disclosure statements, deeds of consent to assignment, guarantees and security, renewals and rent review disputes for smaller Queensland landlords and owner-operators.",
  },
];

const CHECKS = [
  "Term, options to renew and the notice dates that must be met to exercise them",
  "Rent review mechanism — CPI, fixed percentage or market review, and any ratchet clause",
  "Outgoings, promotion and marketing levies, apportionment and audit rights",
  "Permitted use, trading hours, exclusivity, relocation and demolition clauses",
  "Make-good and reinstatement at the end of the term — often the biggest hidden liability",
  "Personal guarantees, bank guarantees and security deposits",
  "Assignment rights and whether the outgoing tenant remains liable",
];

export const Route = createFileRoute("/retail-shop-leases")({
  head: () => ({
    meta: [
      { title: fitTitle(`Retail Shop Lease Lawyers QLD | Form 12 Legal Advice Report`) },
      {
        name: "description",
        content:
          fitDescription("Queensland retail shop lease lawyers. Form 12 legal advice reports for lease assignments, Form 7 disclosure, landlord consent and deeds of assignment — fixed fee, fast turnaround."),
      },
      { property: "og:title", content: fitTitle(`Retail Shop Lease Lawyers Queensland | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Form 12 legal advice reports, disclosure statements and landlord consent for retail shop lease assignments in Queensland."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/retail-shop-leases" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/retail-shop-leases" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: RetailShopLeasesPage,
});

function RetailShopLeasesPage() {
  return (
    <>
      <PageHero
        eyebrow="Business & commercial"
        title="Retail shop leases and Form 12 legal advice reports"
        lead="If you are taking over a shop lease in Queensland, the landlord will not consent until the paperwork under the Retail Shop Leases Act 1994 is done — including a Form 12 legal advice report signed by a lawyer. Lease review and Form 12: $880 incl GST, usually within 1 to 2 business days."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">
          What is a Form 12 legal advice report?
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A Form 12 is the approved form under the Retail Shop Leases Act 1994 (Qld) in which a
          lawyer certifies that they have explained the retail shop lease — and, on an assignment,
          the assignment documents and the disclosure statement — to the incoming tenant. The lawyer
          signs it, not the tenant, and it goes to the landlord as part of the consent process.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The purpose is protective. A small business tenant signing a multi-year lease with rent
          reviews, outgoings, make-good obligations and a personal guarantee should have had it
          explained by someone independent of the landlord and the agent. The Form 12 is the evidence
          that happened — and, in practice, the last thing standing between a business sale and
          settlement.
        </p>

        <div className="mt-10 rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h3 className="font-serif text-xl text-foreground">The forms you will meet</h3>
          <ul className="mt-4 space-y-2.5">
            {[
              "Form 7 — landlord's disclosure statement to a prospective tenant",
              "Form 8 — waiver notice where disclosure is waived",
              "Form 9 — assignor's disclosure statement to the assignee",
              "Form 10 and Form 11 — landlord and assignee disclosure on assignment",
              "Form 12 — legal advice report, signed by the lawyer",
              "Financial advice report — signed by the incoming tenant's accountant",
            ].map((item) => (
              <li
                key={item}
                className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Which forms your transaction needs depends on whether the premises are a retail shop
            under the Act, whether it is a new lease or an assignment, and whether disclosure has
            been waived. We confirm that first, before anyone spends money producing forms they do
            not need.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Who we act for</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {WE_ACT_FOR.map((item) => (
            <div key={item.title} className="rounded-sm border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          What we go through before we sign
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The report is not a formality. Before we certify anything we take the incoming tenant
          through the terms that decide whether the business is viable:
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {CHECKS.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Fixed fee, fast turnaround</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Send us the lease, the disclosure statement and the deed of assignment and the fixed fee is
            $880 incl GST for the lease review and Form 12. Most are completed within 1 to 2
            business days, and we work to broker and franchisor settlement dates where a deal is
            under pressure. Standalone reports are welcome — there is no obligation to engage us for
            the rest of the transaction.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link
              to="/guides/$slug"
              params={{ slug: "assigning-a-retail-shop-lease-queensland" }}
              className="text-primary underline"
            >
              Guide: assigning a retail shop lease in Queensland
            </Link>
            <Link to="/retail-shop-lease-faqs" className="text-primary underline">
              FAQs: taking over or signing a shop lease
            </Link>
            <Link to="/business-sales" className="text-primary underline">
              Buying or selling a business
            </Link>
            <Link to="/commercial-conveyancing" className="text-primary underline">
              Commercial conveyancing
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who does the work" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Retail shop lease questions we are asked
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Request a Form 12 legal advice report" />
        </div>
      </section>

      <CtaBand
        title="Settlement date under pressure?"
        body={`Call ${FIRM.phone} with the lease in front of you and we will tell you what is required and how fast we can turn it around.`}
      />
      <div className="h-20" />
    </>
  );
}
